import { ApiResponse } from '../../types/api';
import {
  DashboardFilter,
  AdminDashboardData,
  OfficerDashboardData,
  SupervisorDashboardData,
  VendorDashboardData,
  ReportType,
  ExportConfig,
  KPIMetric,
  TrendDataPoint,
  CategoryDistribution,
  DepartmentPerformance,
  SLAComplianceData,
  CostTrendPoint,
  PriorityItem,
  VendorLoadItem,
  ActiveWorkOrderItem,
  VendorPerformanceData,
  MonthlyBillingSummary,
} from '../../types/dashboard';
import { IReportsAdapter } from './IReportsAdapter';
import { hasApiEndpoint } from '../../config/backend';
import { ApiClient } from '../apiClient';

const MOCK_ADMIN_KPIS: KPIMetric[] = [
  { label: 'Total Complaints', value: 245, trend: '+12 this week', trendDirection: 'up', icon: 'FileText' },
  { label: 'Open vs Closed', value: '62%', trend: 'Open: 93 | Closed: 152', trendDirection: 'neutral', icon: 'PieChart' },
  { label: 'SLA Compliance', value: '87%', trend: 'On Time: 213', trendDirection: 'up', icon: 'Clock' },
  { label: 'Total Expenditure', value: 'Rs 4.2L', trend: 'This Month', trendDirection: 'neutral', icon: 'IndianRupee' },
];

const MOCK_COMPLAINT_TREND: TrendDataPoint[] = [
  { month: 'Jan', count: 18, value: 18000 },
  { month: 'Feb', count: 22, value: 22000 },
  { month: 'Mar', count: 15, value: 15000 },
  { month: 'Apr', count: 28, value: 28000 },
  { month: 'May', count: 35, value: 35000 },
  { month: 'Jun', count: 20, value: 20000 },
  { month: 'Jul', count: 32, value: 32000 },
  { month: 'Aug', count: 26, value: 26000 },
  { month: 'Sep', count: 12, value: 12000 },
  { month: 'Oct', count: 0, value: 0 },
  { month: 'Nov', count: 0, value: 0 },
  { month: 'Dec', count: 0, value: 0 },
];

const MOCK_CATEGORY_DIST: CategoryDistribution[] = [
  { category_name: 'AC/HVAC', count: 98, percentage: 40, color: '#38bdf8' },
  { category_name: 'Electrical', count: 61, percentage: 25, color: '#a78bfa' },
  { category_name: 'Plumbing', count: 37, percentage: 15, color: '#34d399' },
  { category_name: 'Civil', count: 29, percentage: 12, color: '#fbbf24' },
  { category_name: 'Others', count: 20, percentage: 8, color: '#94a3b8' },
];

const MOCK_DEPT_PERF: DepartmentPerformance[] = [
  { department_name: 'Production', total_complaints: 85, resolved_complaints: 72, resolution_rate: 84.7, avg_resolution_hours: 18 },
  { department_name: 'Maintenance', total_complaints: 62, resolved_complaints: 55, resolution_rate: 88.7, avg_resolution_hours: 14 },
  { department_name: 'Facilities', total_complaints: 44, resolved_complaints: 38, resolution_rate: 86.4, avg_resolution_hours: 20 },
  { department_name: 'IT', total_complaints: 30, resolved_complaints: 26, resolution_rate: 86.7, avg_resolution_hours: 12 },
  { department_name: 'Others', total_complaints: 24, resolved_complaints: 21, resolution_rate: 87.5, avg_resolution_hours: 16 },
];

const MOCK_SLA_COMPLIANCE: SLAComplianceData[] = [
  { priority: 'Critical', total: 18, on_time: 16, breached: 2, compliance_rate: 88.9 },
  { priority: 'High', total: 45, on_time: 40, breached: 5, compliance_rate: 88.9 },
  { priority: 'Medium', total: 120, on_time: 105, breached: 15, compliance_rate: 87.5 },
  { priority: 'Low', total: 62, on_time: 57, breached: 5, compliance_rate: 91.9 },
];

const MOCK_COST_TREND: CostTrendPoint[] = [
  { month: 'Jan', material: 8000, labor: 5000, service: 2000, total: 15000 },
  { month: 'Feb', material: 10000, labor: 6000, service: 3000, total: 19000 },
  { month: 'Mar', material: 6500, labor: 4500, service: 1500, total: 12500 },
  { month: 'Apr', material: 12000, labor: 8000, service: 4000, total: 24000 },
  { month: 'May', material: 15000, labor: 9000, service: 5000, total: 29000 },
  { month: 'Jun', material: 9000, labor: 5500, service: 2500, total: 17000 },
  { month: 'Jul', material: 14000, labor: 8500, service: 4500, total: 27000 },
  { month: 'Aug', material: 11000, labor: 7000, service: 3500, total: 21500 },
  { month: 'Sep', material: 5000, labor: 3000, service: 1500, total: 9500 },
];

const MOCK_OFFICER_KPIS: KPIMetric[] = [
  { label: 'Unassigned Complaints', value: 18, trend: '+3 today', trendDirection: 'up', icon: 'AlertTriangle' },
  { label: 'Pending Work Orders', value: 24, trend: '5 critical', trendDirection: 'up', icon: 'Clock' },
  { label: 'SLA Warnings', value: 5, trend: '2 breaching soon', trendDirection: 'down', icon: 'AlertCircle' },
  { label: 'Vendor Load', value: 'Balanced', trend: '8 active vendors', trendDirection: 'neutral', icon: 'Building2' },
];

const MOCK_PRIORITIES: PriorityItem[] = [
  { complaint_id: 'CMP-2026-00241', title: 'AC Not Working - Server Room', priority: 'Critical', time_remaining: '2h left', status: 'New' },
  { complaint_id: 'CMP-2026-00239', title: 'Water Leak - Floor 3', priority: 'High', time_remaining: '4h left', status: 'Under Review' },
  { complaint_id: 'WO-2026-00156', title: 'Electrical Panel Repair', priority: 'High', time_remaining: '6h left', status: 'Pending Parts' },
  { complaint_id: 'CMP-2026-00237', title: 'Elevator Stuck - Building B', priority: 'Critical', time_remaining: '1h left', status: 'Assigned' },
];

const MOCK_VENDOR_LOAD: VendorLoadItem[] = [
  { vendor_name: 'CoolTech Services', active_orders: 5, completed_orders: 12, load_status: 'balanced' },
  { vendor_name: 'Alpha Electrical', active_orders: 8, completed_orders: 15, load_status: 'overloaded' },
  { vendor_name: 'QuickFix Plumbing', active_orders: 3, completed_orders: 8, load_status: 'underutilized' },
];

const MOCK_SUPERVISOR_KPIS: KPIMetric[] = [
  { label: "Today's Jobs", value: 8, trend: '+2 new', trendDirection: 'up', icon: 'Wrench' },
  { label: 'Pending Inspection', value: 3, trend: '2 high priority', trendDirection: 'neutral', icon: 'Search' },
  { label: 'Waiting Verification', value: 2, trend: 'Dept confirmation pending', trendDirection: 'neutral', icon: 'Clock' },
  { label: 'Completed This Week', value: 12, trend: '+4 vs last week', trendDirection: 'up', icon: 'CheckCircle2' },
];

const MOCK_ACTIVE_WOS: ActiveWorkOrderItem[] = [
  { work_order_id: 'WO-2026-00089', description: 'AC Compressor Replacement', technician_name: 'Raj', status: 'In Progress' },
  { work_order_id: 'WO-2026-00091', description: 'Pipe Joint Repair', technician_name: 'Amit', status: 'Completed' },
  { work_order_id: 'WO-2026-00092', description: 'Panel Board Rewiring', technician_name: 'Suresh', status: 'Pending Parts' },
];

const MOCK_VENDOR_KPIS: KPIMetric[] = [
  { label: 'Active Work Orders', value: 15, trend: '+3 assigned today', trendDirection: 'up', icon: 'Wrench' },
  { label: 'Pending Completion', value: 4, trend: '2 overdue', trendDirection: 'down', icon: 'Clock' },
  { label: 'Completed This Month', value: 22, trend: '+8 vs last month', trendDirection: 'up', icon: 'CheckCircle2' },
  { label: 'Monthly Billing', value: 'Rs 2.8L', trend: '3 invoices pending', trendDirection: 'neutral', icon: 'IndianRupee' },
];

const MOCK_VENDOR_PERFORMANCE: VendorPerformanceData = {
  avg_response_time: 2.3,
  on_time_completion: 94,
  inspection_pass_rate: 89,
  total_completed: 22,
  total_assigned: 25,
  rating: 4.2,
};

const MOCK_MONTHLY_BILLING: MonthlyBillingSummary[] = [
  { month: 'Jul', total_billed: 180000, total_paid: 150000, pending_amount: 30000 },
  { month: 'Aug', total_billed: 220000, total_paid: 180000, pending_amount: 40000 },
  { month: 'Sep', total_billed: 280000, total_paid: 100000, pending_amount: 180000 },
];

const MOCK_REPORT_TYPES: ReportType[] = [
  { id: 'dept-wise', name: 'Department-wise Report', description: 'Complaints and work orders by department', category: 'Complaints' },
  { id: 'cat-wise', name: 'Category-wise Report', description: 'Breakdown by complaint category', category: 'Complaints' },
  { id: 'loc-wise', name: 'Location-wise Report', description: 'Geographic distribution of complaints', category: 'Complaints' },
  { id: 'vendor-wise', name: 'Vendor Performance Report', description: 'Vendor performance comparison', category: 'Vendors' },
  { id: 'supervisor-wise', name: 'Supervisor Workload Report', description: 'Supervisor workload comparison', category: 'Workforce' },
  { id: 'monthly-trend', name: 'Monthly Trend Report', description: 'Month-over-month analysis', category: 'Analytics' },
  { id: 'sla-compliance', name: 'SLA Compliance Report', description: 'SLA performance by priority level', category: 'Analytics' },
  { id: 'cost-analysis', name: 'Cost Analysis Report', description: 'Expenditure breakdown by category', category: 'Finance' },
  { id: 'repeat-complaints', name: 'Repeat Complaints Report', description: 'Recurring issues identification', category: 'Analytics' },
];

const MOCK_REPORT_DATA: Record<string, Record<string, unknown>[]> = {
  'dept-wise': [
    { department: 'Production', total_complaints: 85, resolved: 72, open: 13, resolution_rate: '84.7%' },
    { department: 'Maintenance', total_complaints: 62, resolved: 55, open: 7, resolution_rate: '88.7%' },
    { department: 'Facilities', total_complaints: 44, resolved: 38, open: 6, resolution_rate: '86.4%' },
    { department: 'IT', total_complaints: 30, resolved: 26, open: 4, resolution_rate: '86.7%' },
    { department: 'HR', total_complaints: 14, resolved: 12, open: 2, resolution_rate: '85.7%' },
    { department: 'Stores', total_complaints: 10, resolved: 9, open: 1, resolution_rate: '90.0%' },
  ],
  'cat-wise': [
    { category: 'AC/HVAC', total: 98, open: 32, closed: 66, avg_resolution_days: 2.1 },
    { category: 'Electrical', total: 61, open: 18, closed: 43, avg_resolution_days: 1.8 },
    { category: 'Plumbing', total: 37, open: 12, closed: 25, avg_resolution_days: 1.5 },
    { category: 'Civil', total: 29, open: 15, closed: 14, avg_resolution_days: 3.2 },
    { category: 'Elevator/Lift', total: 12, open: 4, closed: 8, avg_resolution_days: 2.8 },
    { category: 'Fire Safety', total: 8, open: 2, closed: 6, avg_resolution_days: 1.2 },
  ],
  'vendor-wise': [
    { vendor: 'CoolTech Services', assigned: 20, completed: 18, on_time: 17, rating: 4.5, avg_response_hrs: 1.8 },
    { vendor: 'Alpha Electrical', assigned: 25, completed: 22, on_time: 20, rating: 4.2, avg_response_hrs: 2.3 },
    { vendor: 'QuickFix Plumbing', assigned: 15, completed: 14, on_time: 12, rating: 3.8, avg_response_hrs: 3.1 },
    { vendor: 'BuildRight Civil', assigned: 12, completed: 10, on_time: 9, rating: 4.0, avg_response_hrs: 2.5 },
  ],
  'sla-compliance': [
    { priority: 'Critical', total: 18, on_time: 16, breached: 2, compliance: '88.9%', avg_resolution_hrs: 3.2 },
    { priority: 'High', total: 45, on_time: 40, breached: 5, compliance: '88.9%', avg_resolution_hrs: 6.8 },
    { priority: 'Medium', total: 120, on_time: 105, breached: 15, compliance: '87.5%', avg_resolution_hrs: 18.5 },
    { priority: 'Low', total: 62, on_time: 57, breached: 5, compliance: '91.9%', avg_resolution_hrs: 48.2 },
  ],
  'cost-analysis': [
    { category: 'AC/HVAC', material_cost: 120000, labor_cost: 65000, service_cost: 25000, total: 210000 },
    { category: 'Electrical', material_cost: 45000, labor_cost: 35000, service_cost: 12000, total: 92000 },
    { category: 'Plumbing', material_cost: 28000, labor_cost: 18000, service_cost: 8000, total: 54000 },
    { category: 'Civil', material_cost: 35000, labor_cost: 22000, service_cost: 10000, total: 67000 },
  ],
};

export class GASReportsAdapter implements IReportsAdapter {
  async getAdminDashboard(filter?: DashboardFilter): Promise<ApiResponse<AdminDashboardData>> {
    if (!hasApiEndpoint()) {
      let trend = [...MOCK_COMPLAINT_TREND];
      let categoryDist = [...MOCK_CATEGORY_DIST];
      let deptPerf = [...MOCK_DEPT_PERF];

      if (filter?.department_id) {
        deptPerf = deptPerf.filter(d => d.department_name.toLowerCase().includes(filter.department_id!.toLowerCase()));
      }

      return {
        success: true,
        data: {
          kpis: MOCK_ADMIN_KPIS,
          complaint_trend: trend,
          category_distribution: categoryDist,
          department_performance: deptPerf,
          sla_compliance: MOCK_SLA_COMPLIANCE,
          cost_trend: MOCK_COST_TREND,
        },
        message: 'Admin dashboard loaded',
      };
    }
    const params = new URLSearchParams();
    if (filter?.department_id) params.set('department_id', filter.department_id);
    if (filter?.status) params.set('status', filter.status);
    if (filter?.priority) params.set('priority', filter.priority);
    if (filter?.date_from) params.set('from', filter.date_from);
    if (filter?.date_to) params.set('to', filter.date_to);
    const qs = params.toString();
    const res = await ApiClient.get<AdminDashboardData>(`/reports/dashboard/admin${qs ? `?${qs}` : ''}`);
    return res;
  }

  async getOfficerDashboard(filter?: DashboardFilter): Promise<ApiResponse<OfficerDashboardData>> {
    if (!hasApiEndpoint()) {
      return {
        success: true,
        data: {
          kpis: MOCK_OFFICER_KPIS,
          today_priorities: MOCK_PRIORITIES,
          vendor_load: MOCK_VENDOR_LOAD,
        },
        message: 'Officer dashboard loaded',
      };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async getSupervisorDashboard(filter?: DashboardFilter): Promise<ApiResponse<SupervisorDashboardData>> {
    if (!hasApiEndpoint()) {
      return {
        success: true,
        data: {
          kpis: MOCK_SUPERVISOR_KPIS,
          active_work_orders: MOCK_ACTIVE_WOS,
        },
        message: 'Supervisor dashboard loaded',
      };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async getVendorDashboard(vendorId: string, filter?: DashboardFilter): Promise<ApiResponse<VendorDashboardData>> {
    if (!hasApiEndpoint()) {
      return {
        success: true,
        data: {
          kpis: MOCK_VENDOR_KPIS,
          performance: MOCK_VENDOR_PERFORMANCE,
          monthly_billing: MOCK_MONTHLY_BILLING,
        },
        message: 'Vendor dashboard loaded',
      };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async getReportTypes(): Promise<ApiResponse<ReportType[]>> {
    if (!hasApiEndpoint()) {
      return { success: true, data: MOCK_REPORT_TYPES, message: 'Report types loaded' };
    }
    return { success: false, data: [], message: 'API not configured' };
  }

  async getReportData(reportId: string, filter?: DashboardFilter): Promise<ApiResponse<Record<string, unknown>[]>> {
    if (!hasApiEndpoint()) {
      const data = MOCK_REPORT_DATA[reportId] || [];
      return { success: true, data, message: 'Report data loaded' };
    }
    return { success: false, data: [], message: 'API not configured' };
  }

  async exportToExcel(config: ExportConfig): Promise<ApiResponse<Blob>> {
    return { success: true, data: new Blob(), message: 'Export handled by excelExport utility' };
  }
}
